<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import OperationsAPI from 'dashboard/api/operationsStream';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import messages from './operationMessages';
import timingMessages from './operationTimingMessages';

// Keep this Russian MedUp module independent of Chatwoot's default English locale.
const { t, te, locale } = useI18n({
  useScope: 'local',
  inheritLocale: false,
  locale: 'ru',
  fallbackLocale: 'ru',
  messages: {
    ru: { ...messages.ru, ...timingMessages.ru },
    en: { ...messages.en, ...timingMessages.en },
  },
});
const route = useRoute();
const rows = ref([]);
const entities = ref([]);
const meta = ref({ page: 1, total: 0 });
const received = ref(null);
const timingSummary = ref(null);
const loading = ref(false);
const error = ref(false);
const filters = reactive({
  source_kind: '',
  entity_type: '',
  event_type: '',
  status: '',
  from: '',
  to: '',
});
const filterLabels = {
  source_kind: 'source',
  entity_type: 'entity',
  event_type: 'event',
  status: 'status',
};
const filterGroups = {
  source_kind: 'sources',
  entity_type: 'entities',
  event_type: 'events',
  status: 'statuses',
};
const options = key =>
  ({
    source_kind: ['mres3', 'scheduler', 'generic'],
    entity_type: entities.value,
    event_type: [
      'created',
      'updated',
      'deleted',
      'changed',
      'response',
      'scheduled',
    ],
    status: ['pending', 'retrying', 'processed', 'suppressed', 'failed'],
  })[key];
const dialog = ref(null);
const detail = ref(null);
const detailLoading = ref(false);
const detailError = ref(false);
const showAllFields = ref(true);
const detailFields = computed(
  () =>
    (showAllFields.value ? detail.value?.fields : detail.value?.changes) || []
);
let request = 0;
let detailRequest = 0;
const label = (group, value) =>
  te(group + '.' + value) ? t(group + '.' + value) : value || '—';
const fmt = value =>
  value
    ? new Date(value).toLocaleString(locale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';
const displayZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timingDates = [
  ['misCreated', 'mis_created_at'],
  ['mresCreated', 'mres_created_at'],
  ['crmCreated', 'crm_created_at'],
  ['cdpCreated', 'cdp_created_at'],
];
const fmtTiming = value =>
  value
    ? new Date(value).toLocaleString(locale.value, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
      })
    : t('timingMissing');
const duration = milliseconds => {
  if (milliseconds === null || milliseconds === undefined)
    return t('timingMissing');
  if (milliseconds < 0) return t('clockMismatch');
  const units = [
    [86400000, 'timingDays'],
    [3600000, 'timingHours'],
    [60000, 'timingMinutes'],
    [1000, 'timingSeconds'],
  ];
  const [divisor, key] =
    units.find(([size]) => milliseconds >= size) || units[3];
  return t(key, {
    value: (milliseconds / divisor).toLocaleString(locale.value, {
      maximumFractionDigits: 3,
    }),
  });
};
const processing = row =>
  row.processing?.status === 'processed'
    ? label('outcomes', row.processing.outcome)
    : label('statuses', row.processing?.status);
const contactPath = id =>
  '/app/accounts/' + route.params.accountId + '/contacts/' + id;
const scenarioPath = id =>
  '/app/accounts/' + route.params.accountId + '/automation/scenarios/' + id;
const valueLabel = (value, known, field) => {
  if (!known) return t('unknown');
  if (value === null || value === undefined || value === '') return t('blank');
  if (typeof value === 'boolean') {
    if (field?.startsWith('is_second')) return t(value ? 'repeat' : 'primary');
    return t(value ? 'yes' : 'no');
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};
const dateBoundary = (value, nextDay = false) => {
  if (!value) return undefined;
  const date = new Date(value + 'T00:00:00');
  if (nextDay) date.setDate(date.getDate() + 1);
  return date.toISOString();
};
const load = async (page = 1, refresh = false) => {
  request += 1;
  const token = request;
  loading.value = true;
  error.value = false;
  try {
    const { from, to, ...selected } = filters;
    const { data } = await OperationsAPI.list({
      ...selected,
      page,
      per: 50,
      from_at: dateBoundary(from),
      until_at: dateBoundary(to, true),
      snapshot_id: refresh ? undefined : meta.value.snapshot_id,
    });
    if (token !== request) return;
    rows.value = data.operations;
    entities.value = data.entities;
    meta.value = data.meta;
    received.value = data.latest_received_at;
    timingSummary.value = data.timing_summary;
  } catch {
    if (token === request) error.value = true;
  } finally {
    if (token === request) loading.value = false;
  }
};
const refresh = () => load(1, true);
const reset = () => {
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
  refresh();
};
const openDetail = async row => {
  detailRequest += 1;
  const token = detailRequest;
  detail.value = null;
  showAllFields.value = true;
  detailError.value = false;
  detailLoading.value = true;
  dialog.value.open();
  try {
    const { data } = await OperationsAPI.detail(row.id);
    if (token === detailRequest) detail.value = data;
  } catch {
    if (token === detailRequest) detailError.value = true;
  } finally {
    if (token === detailRequest) detailLoading.value = false;
  }
};
const closeDetail = () => {
  detailRequest += 1;
};
onMounted(refresh);
onBeforeUnmount(() => {
  request += 1;
  detailRequest += 1;
});
</script>

<template>
  <div
    class="flex flex-col w-full h-full overflow-auto bg-n-surface-1 text-n-slate-12"
  >
    <div
      class="flex flex-col items-start justify-between gap-4 px-6 py-5 border-b border-n-weak sm:flex-row"
    >
      <div class="w-full min-w-0 flex-1">
        <h1 class="mb-1 text-xl font-medium">{{ t('title') }}</h1>
        <p class="mb-0 text-sm text-n-slate-11">{{ t('subtitle') }}</p>
        <p v-if="received" class="mt-2 mb-0 text-xs text-n-slate-10">
          {{ t('received', { date: fmt(received) }) }}
        </p>
        <section
          v-if="timingSummary"
          class="mt-4"
          :aria-label="t('averageTiming')"
        >
          <h2 class="mb-2 text-xs font-medium text-n-slate-11">
            {{ t('averageTiming') }}
          </h2>
          <div class="grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            <div
              v-for="window in timingSummary.windows"
              :key="window.hours"
              class="px-4 py-3 border rounded-xl border-n-weak bg-n-surface-2"
            >
              <p class="mb-1 text-xs text-n-slate-11">
                {{ t('averagePeriods.' + window.hours) }}
              </p>
              <p class="mb-1 text-xl font-semibold tabular-nums">
                {{ duration(window.average_ms) }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{
                  t('averageSample', {
                    count: window.sample_count,
                    total: window.candidate_count,
                  })
                }}
              </p>
              <p
                v-if="window.missing_timezone_count"
                class="mt-1 mb-0 text-xs text-n-slate-11"
              >
                {{ t('averageTimezoneMissing') }}
              </p>
            </div>
          </div>
          <p class="mt-2 mb-0 text-xs text-n-slate-10">
            {{ t('averageHint') }}
          </p>
          <p class="mt-1 mb-0 text-xs text-n-slate-10">
            {{ t('averageAsOf', { date: fmt(timingSummary.computed_at) }) }}
          </p>
        </section>
      </div>
      <Button
        class="order-first self-end sm:order-last sm:self-start"
        icon="i-lucide-refresh-cw"
        variant="faded"
        color="slate"
        :label="t('refresh')"
        :is-loading="loading"
        @click="refresh"
      />
    </div>
    <div class="px-6 py-4">
      <div class="flex flex-wrap items-end gap-3 mb-4">
        <label
          v-for="filter in Object.keys(filterLabels)"
          :key="filter"
          class="text-xs text-n-slate-11"
        >
          {{ t(filterLabels[filter]) }}
          <select
            v-model="filters[filter]"
            class="block min-w-36 h-10 mt-1 !mb-0 text-sm border-n-weak rounded-lg bg-n-solid-1 !bg-none !appearance-auto"
            @change="refresh"
          >
            <option value="">{{ t('all') }}</option>
            <option
              v-for="option in options(filter)"
              :key="option"
              :value="option"
            >
              {{ label(filterGroups[filter], option) }}
            </option>
          </select>
        </label>
        <label
          v-for="boundary in ['from', 'to']"
          :key="boundary"
          class="text-xs text-n-slate-11"
        >
          {{ t(boundary) }}
          <input
            v-model="filters[boundary]"
            type="date"
            class="block h-10 mt-1 !mb-0 text-sm border-n-weak rounded-lg bg-n-solid-1"
            @change="refresh"
          />
        </label>
        <Button
          variant="ghost"
          color="slate"
          :label="t('reset')"
          @click="reset"
        />
      </div>
      <div v-if="error" role="alert" class="py-10 text-center text-n-ruby-11">
        {{ t('error') }}
      </div>
      <div
        v-else-if="loading"
        role="status"
        class="py-10 text-center text-n-slate-11"
      >
        {{ t('loading') }}
      </div>
      <div v-else-if="!rows.length" class="py-12 text-center text-n-slate-11">
        {{ t('empty') }}
      </div>
      <div
        v-else
        class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1"
      >
        <table class="w-full text-sm text-left border-collapse">
          <thead class="text-xs text-n-slate-11 bg-n-surface-2">
            <tr>
              <th
                v-for="column in [
                  'when',
                  'cdpReceived',
                  'fullPath',
                  'source',
                  'event',
                  'entity',
                  'patient',
                  'status',
                  'runs',
                  'details',
                ]"
                :key="column"
                class="px-3 py-3 font-medium whitespace-nowrap"
              >
                {{ t(column) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
              class="border-t border-n-weak hover:bg-n-slate-2"
            >
              <td
                class="px-3 py-3 whitespace-nowrap tabular-nums text-n-slate-11"
              >
                {{ fmt(row.occurred_at) }}
              </td>
              <td
                class="px-3 py-3 whitespace-nowrap tabular-nums text-n-slate-11"
              >
                {{ fmtTiming(row.timing?.event_received_at) }}
              </td>
              <td class="px-3 py-3 whitespace-nowrap tabular-nums">
                <button
                  v-if="row.timing"
                  type="button"
                  class="text-n-blue-11 hover:underline focus-visible:underline"
                  :title="t('fullPathHint')"
                  @click="openDetail(row)"
                >
                  {{ duration(row.timing.mis_to_cdp_ms) }}
                </button>
                <span v-else class="text-n-slate-11">—</span>
              </td>
              <td class="px-3 py-3 text-n-slate-11">
                {{ label('sources', row.source_kind) }}
              </td>
              <td class="px-3 py-3 font-medium whitespace-nowrap">
                <button
                  type="button"
                  class="text-left text-n-blue-11 hover:underline focus-visible:underline"
                  :title="t('openEvent')"
                  @click="openDetail(row)"
                >
                  {{
                    row.event_type ? label('events', row.event_type) : row.name
                  }}
                </button>
              </td>
              <td class="px-3 py-3">
                <div>{{ label('entities', row.entity_type) }}</div>
                <div
                  v-if="row.source_record_id"
                  class="text-xs text-n-slate-10"
                >
                  #{{ row.source_record_id }}
                </div>
              </td>
              <td class="px-3 py-3 min-w-40">
                <router-link
                  v-if="row.contact"
                  :to="contactPath(row.contact.id)"
                  class="text-n-blue-11 hover:underline"
                >
                  {{ row.contact.name }}
                </router-link>
                <span v-else class="text-n-slate-11">{{
                  row.subject_name || '—'
                }}</span>
              </td>
              <td
                class="px-3 py-3 min-w-44 text-xs"
                :class="
                  row.processing?.status === 'failed'
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-11'
                "
              >
                {{ processing(row) }}
              </td>
              <td class="px-3 py-3 tabular-nums">{{ row.runs_count }}</td>
              <td class="px-3 py-3">
                <Button
                  variant="ghost"
                  color="slate"
                  icon="i-lucide-chevron-right"
                  size="sm"
                  :label="t('details')"
                  @click="openDetail(row)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-n-slate-11"
      >
        <span>{{
          t('total', { count: meta.total.toLocaleString(locale) })
        }}</span>
        <div class="flex items-center gap-3">
          <Button
            variant="faded"
            color="slate"
            :label="t('back')"
            :disabled="meta.page <= 1 || loading"
            @click="load(meta.page - 1)"
          />
          <span>{{ t('page', { page: meta.page }) }}</span>
          <Button
            variant="faded"
            color="slate"
            :label="t('next')"
            :disabled="!meta.has_more || loading"
            @click="load(meta.page + 1)"
          />
        </div>
      </div>
    </div>
    <Dialog
      ref="dialog"
      :title="t('details')"
      width="3xl"
      :show-confirm-button="false"
      :cancel-button-label="t('close')"
      @close="closeDetail"
    >
      <div class="max-h-[65vh] overflow-auto text-sm">
        <p v-if="detailLoading" role="status">{{ t('loading') }}</p>
        <p v-else-if="detailError" role="alert" class="text-n-ruby-11">
          {{ t('error') }}
        </p>
        <template v-else-if="detail">
          <h4 class="mb-3 font-medium">
            {{ label('entities', detail.entity_type) }} ·
            {{
              detail.event_type
                ? label('events', detail.event_type)
                : detail.name
            }}
          </h4>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-2 mb-5 text-xs">
            <dt class="text-n-slate-11">{{ t('occurredAt') }}</dt>
            <dd>{{ fmt(detail.occurred_at) }}</dd>
            <dt class="text-n-slate-11">{{ t('receivedAt') }}</dt>
            <dd>{{ fmt(detail.received_at) }}</dd>
            <dt class="text-n-slate-11">{{ t('status') }}</dt>
            <dd>{{ processing(detail) }}</dd>
          </dl>
          <section
            v-if="detail.timing"
            class="p-4 mb-5 border rounded-xl border-n-weak bg-n-surface-2"
          >
            <h4 class="mb-2 font-medium">{{ t('timingTitle') }}</h4>
            <div class="flex items-center justify-between gap-3 mb-3">
              <span class="font-medium">{{ t('fullPath') }}</span>
              <span class="text-lg font-semibold tabular-nums">{{
                duration(detail.timing.mis_to_cdp_ms)
              }}</span>
            </div>
            <p class="mb-3 text-xs text-n-slate-11">{{ t('timingHint') }}</p>
            <dl class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div
                v-for="[title, field] in timingDates"
                :key="field"
                class="p-3 rounded-lg bg-n-solid-1"
              >
                <dt class="mb-1 text-xs text-n-slate-11">{{ t(title) }}</dt>
                <dd class="m-0 text-xs font-medium tabular-nums break-words">
                  {{
                    field === 'crm_created_at'
                      ? t('crmNotSent')
                      : field === 'mis_created_at' &&
                          detail.timing.mis_timezone_unknown
                        ? detail.timing.mis_created_raw
                        : fmtTiming(detail.timing[field])
                  }}
                </dd>
              </div>
            </dl>
            <p class="mt-2 mb-3 text-xs text-n-slate-11">
              {{ t('timingTimezone', { zone: displayZone }) }}
            </p>
            <p
              v-if="detail.timing.mis_timezone_unknown"
              class="mb-3 text-xs text-n-slate-11"
            >
              {{ t('misTimezoneUnknown') }}
            </p>
            <dl class="grid grid-cols-2 gap-2 text-xs">
              <dt class="text-n-slate-11">
                {{ t('eventDelayRoute') }}
              </dt>
              <dd class="m-0 font-medium tabular-nums">
                {{ duration(detail.timing.event_delay_ms) }}
              </dd>
              <template
                v-for="[title, field] in [
                  ['misToMres', 'mis_to_mres_ms'],
                  ['mresToCdp', 'mres_to_cdp_ms'],
                  ['misToCdp', 'mis_to_cdp_ms'],
                ]"
                :key="field"
              >
                <dt class="text-n-slate-11">{{ t(title) }}</dt>
                <dd class="m-0 tabular-nums">
                  {{ duration(detail.timing[field]) }}
                </dd>
              </template>
            </dl>
            <p class="mt-3 mb-0 text-xs text-n-slate-11">
              {{ t('firstArrivalHint') }}
            </p>
          </section>
          <p
            v-if="detail.imported && detail.processing?.status === 'suppressed'"
            class="p-3 text-xs rounded-lg bg-n-surface-2 text-n-slate-11"
          >
            {{ t('history') }}
          </p>
          <p
            v-if="detail.history === 'metadata_only'"
            class="p-3 text-xs rounded-lg bg-n-surface-2 text-n-slate-11"
          >
            {{ t('incomplete') }}
          </p>
          <p
            v-if="detail.history === 'last_known'"
            class="text-xs text-n-slate-11"
          >
            {{ t('lastKnown') }}
          </p>
          <h4 class="mt-5 mb-2 font-medium">
            {{ t('eventData') }}
          </h4>
          <p
            v-if="detail.history !== 'metadata_only'"
            class="mb-3 text-xs text-n-slate-11"
          >
            {{ t('snapshotHint') }}
          </p>
          <div
            v-if="detail.fields.length"
            class="flex gap-2 mb-3"
            role="group"
            :aria-label="t('dataView')"
          >
            <Button
              type="button"
              size="sm"
              color="slate"
              :variant="showAllFields ? 'faded' : 'ghost'"
              :aria-pressed="showAllFields"
              :label="t('allFields', { count: detail.fields.length })"
              @click="showAllFields = true"
            />
            <Button
              type="button"
              size="sm"
              color="slate"
              :variant="showAllFields ? 'ghost' : 'faded'"
              :aria-pressed="!showAllFields"
              :label="t('onlyChanges', { count: detail.changes.length })"
              @click="showAllFields = false"
            />
          </div>
          <div
            v-if="detailFields.length"
            class="overflow-auto border rounded-lg border-n-weak"
          >
            <table class="w-full text-xs text-left table-fixed">
              <thead class="bg-n-surface-2 text-n-slate-11">
                <tr>
                  <th
                    v-for="column in ['field', 'before', 'after']"
                    :key="column"
                    class="p-2 font-medium"
                  >
                    {{ t(column) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="change in detailFields"
                  :key="change.field"
                  class="border-t border-n-weak"
                >
                  <td class="p-2 align-top break-words">{{ change.label }}</td>
                  <td class="p-2 align-top break-words text-n-slate-11">
                    {{
                      valueLabel(
                        change.before,
                        change.before_known,
                        change.field
                      )
                    }}
                  </td>
                  <td class="p-2 align-top break-words">
                    {{
                      valueLabel(change.after, change.after_known, change.field)
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p
            v-else-if="!showAllFields && detail.history !== 'metadata_only'"
            class="text-xs text-n-slate-11"
          >
            {{ t('noChanges') }}
          </p>
          <h4 class="mt-5 mb-2 font-medium">{{ t('runs') }}</h4>
          <p v-if="!detail.runs.length" class="text-xs text-n-slate-11">
            {{ t('noRuns') }}
          </p>
          <div
            v-for="run in detail.runs"
            :key="run.id"
            class="flex justify-between gap-3 py-2 border-b border-n-weak"
          >
            <router-link
              class="text-n-blue-11 hover:underline"
              :to="scenarioPath(run.scenario_id)"
            >
              {{ run.scenario_name
              }}<span v-if="run.version" class="ml-2 text-xs text-n-slate-11">{{
                t('version', { version: run.version })
              }}</span>
            </router-link>
            <span class="text-xs text-n-slate-11">{{
              label('runStatuses', run.status)
            }}</span>
          </div>
          <details v-if="detail.payload" class="mt-4 text-xs">
            <summary>{{ t('technical') }}</summary>
            <pre class="mt-2 whitespace-pre-wrap break-all">{{
              JSON.stringify(detail.payload, null, 2)
            }}</pre>
          </details>
        </template>
      </div>
    </Dialog>
  </div>
</template>
